import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UsersService } from './users.service';
import { Permission } from '../models/permission.model';
import { CurrentUserResponse } from '../models/user.model';

/**
 * PermissionService manages user permissions based on their role
 * in the currently selected business unit.
 *
 * Responsibilities:
 * - Load permissions from the current user's active business unit role
 * - Provide real-time permission checking methods for templates and components
 * - Support permission refreshing when switching business units
 * - React to business unit changes automatically
 *
 * Integration:
 * - Reads from UsersService.getCurrentUser() which returns active_business_unit
 * - Active business unit contains the role with all permissions
 * - Automatically reloads when business unit selector emits change
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionService implements OnDestroy {
  private permissionsSubject = new BehaviorSubject<Set<string>>(new Set());
  private activeBusinessUnitIdSubject = new BehaviorSubject<number | null>(null);
  private applicationSubject = new BehaviorSubject<string>('cms');
  private currentUserResponseSubject = new BehaviorSubject<CurrentUserResponse | null>(null);
  private destroy$ = new Subject<void>();

  constructor(private usersService: UsersService) {
    this.loadPermissions();
  }

  /**
   * Load permissions based on the current user's active business unit
   * from the API response. The API returns active_business_unit which
   * contains the user's selected role and all associated permissions.
   */
  loadPermissions(): void {
    this.usersService.getCurrentUser().subscribe({
      next: (response) => {
        // Store the full response for other components to use
        this.currentUserResponseSubject.next(response);
        
        const permissions = this.extractPermissionsFromResponse(response);
        this.permissionsSubject.next(permissions);
      },
      error: (error) => {
        console.error('Failed to load permissions:', error);
        this.permissionsSubject.next(new Set());
      },
    });
  }

  /**
   * Load permissions asynchronously and return as Observable.
   * Useful for components that need to wait for permissions to load.
   */
  loadPermissionsAsync(): Observable<string[]> {
    return this.usersService.getCurrentUser().pipe(
      map((response) => {
        // Store the full response for other components to use
        this.currentUserResponseSubject.next(response);
        
        const permissions = this.extractPermissionsFromResponse(response);
        this.permissionsSubject.next(permissions);
        return Array.from(permissions);
      })
    );
  }

  /**
   * Reload permissions - call this after login or business unit switch.
   * This fetches the latest user data with the newly selected business unit.
   * Returns a Promise that resolves when permissions are loaded.
   */
  async reload(): Promise<void> {
    await firstValueFrom(this.loadPermissionsAsync());
  }

  /**
   * Trigger permission reload when business unit changes.
   * This is called by the BusinessUnitSelectorComponent when user switches PBU.
   * Returns a Promise that resolves when permissions are loaded.
   */
  onBusinessUnitChanged(): Promise<void> {
    return this.reload();
  }

  /**
   * Synchronously read the latest cached CurrentUserResponse.
   * Useful for building optimistic updates without an extra API call.
   */
  getCurrentUserResponseSnapshot(): CurrentUserResponse | null {
    return this.currentUserResponseSubject.value;
  }

  /**
   * Directly apply a CurrentUserResponse (e.g. from an optimistic update)
   * without making an API call.
   */
  applyCurrentUserResponse(response: CurrentUserResponse): void {
    this.currentUserResponseSubject.next(response);
    const permissions = this.extractPermissionsFromResponse(response);
    this.permissionsSubject.next(permissions);
  }

  /**
   * Check if the user has a specific permission.
   *
   * @param permissionName The exact name of the permission to check
   * @returns true if the user has the permission, false otherwise
   *
   * Example:
   * ```typescript
   * if (this.permissionService.hasPermission('Edit Metadata')) {
   *   // Show edit button
   * }
   * ```
   */
  hasPermission(permissionName: string): boolean {
    return this.permissionsSubject.value.has(permissionName);
  }

  /**
   * Check if the user has ANY of the specified permissions.
   *
   * @param permissionNames Array of permission names to check
   * @returns true if the user has at least one of the permissions, false otherwise
   *
   * Example:
   * ```typescript
   * if (this.permissionService.hasAnyPermission(['Edit Metadata', 'Delete Assets'])) {
   *   // Show edit or delete button
   * }
   * ```
   */
  hasAnyPermission(permissionNames: string[]): boolean {
    return permissionNames.some((name) => this.permissionsSubject.value.has(name));
  }

  /**
   * Check if the user has ALL of the specified permissions.
   *
   * @param permissionNames Array of permission names to check
   * @returns true if the user has all of the permissions, false otherwise
   */
  hasAllPermissions(permissionNames: string[]): boolean {
    return permissionNames.every((name) => this.permissionsSubject.value.has(name));
  }

  /**
   * Get all permissions for the current user and selected business unit.
   *
   * @returns Array of permission names
   */
  getAllPermissions(): string[] {
    return Array.from(this.permissionsSubject.value);
  }

  /**
   * Get permissions as an Observable for reactive components.
   * Emits immediately with current permissions and whenever permissions change.
   *
   * @returns Observable of permission name strings
   *
   * Example:
   * ```typescript
   * this.permissions$ = this.permissionService.getPermissions$();
   * ```
   */
  getPermissions$(): Observable<string[]> {
    return this.permissionsSubject.asObservable().pipe(
      map((permissions) => Array.from(permissions))
    );
  }

  /**
   * Get the currently active business unit ID.
   * This changes when the user switches to a different PBU.
   */
  getActiveBusinessUnitId$(): Observable<number | null> {
    return this.activeBusinessUnitIdSubject.asObservable();
  }

  /**
   * Get the application type for the current role ('cms' or 'app').
   */
  getApplication$(): Observable<string> {
    return this.applicationSubject.asObservable();
  }

  /**
   * Get the full CurrentUserResponse from the last API call.
   * This contains user data, business units, and active business unit info.
   * Use this to avoid duplicate API calls in other components.
   */
  getCurrentUserResponse$(): Observable<CurrentUserResponse | null> {
    return this.currentUserResponseSubject.asObservable();
  }

  /**
   * Extract permissions from the API response.
   * The response structure includes active_business_unit which contains:
   * - role_id, business_unit_id
   * - role object with permissions array
   * - business_unit object with id and name
   *
   * @param userResponse The CurrentUserResponse from the backend
   * @returns Set of permission names from the active business unit's role
   */
  private extractPermissionsFromResponse(userResponse: CurrentUserResponse): Set<string> {
    const permissions = new Set<string>();

    if (!userResponse?.active_business_unit) {
      console.warn('No active business unit found in user response');
      return permissions;
    }

    const activeBusinessUnit = userResponse.active_business_unit;

    // Store active business unit context for reference
    if (activeBusinessUnit.business_unit_id) {
      this.activeBusinessUnitIdSubject.next(activeBusinessUnit.business_unit_id);
    }

    // Extract permissions from the role
    if (activeBusinessUnit.role?.permissions && Array.isArray(activeBusinessUnit.role.permissions)) {
      activeBusinessUnit.role.permissions.forEach((permission: Permission) => {
        if (permission.name) {
          permissions.add(permission.name);
        }
      });
    } else {
      console.warn('No permissions found in active business unit role');
    }

    return permissions;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
